// The hub's password reset with a one-time key (Core#38 point 7, 02.11.2026) -
// the pure part of the page, tested in reset_logic.test.ts. The mail carries
// the key, and a link <address>/passwort-zuruecksetzen#t=<key> when the hub
// knows its public address; the fragment never reaches the server, and only
// the form on the page sets the password.

/** Minimum length of the hub's password rule (PasswordPolicy, default 12). The hub checks again. */
export const MIN_LENGTH = 12;

/** The page that takes the key. */
export const RESET_PAGE = "/passwort-zuruecksetzen";

export const requestPath = (api: string): string => api + "/users/resetpw";
export const confirmPath = (api: string): string => api + "/users/resetpw/confirm";

/** The key from a location fragment "#t=<key>"; "" for anything else. Total. */
export function keyFromFragment(fragment: string | null | undefined): string {
    const text = (fragment ?? "").replace(/^#/, "");
    const value = new URLSearchParams(text).get("t") ?? "";
    return /^[A-Za-z0-9_-]{43}$/.test(value) ? value : "";
}

/** What is wrong with the form before it goes out, or null. Total. */
export function formProblem(key: string, password: string, repeated: string, minLength: number = MIN_LENGTH): string | null {
    if (!/^[A-Za-z0-9_-]{43}$/.test(key.trim())) return "Bitte den Schlüssel aus der Mail vollständig eingeben.";
    if (password.length < minLength) return `Das neue Passwort muss mindestens ${minLength} Zeichen lang sein.`;
    if (password !== repeated) return "Die beiden Eingaben stimmen nicht überein.";
    return null;
}

export type ConfirmOutcome =
    | { kind: "done" }
    | { kind: "policy"; message: string }
    | { kind: "gone"; message: string }
    | { kind: "failed"; message: string };

const GONE: Record<string, string> = {
    expired: "Der Schlüssel ist abgelaufen. Bitte einen neuen anfordern.",
    used: "Der Schlüssel wurde schon benutzt. Bitte einen neuen anfordern.",
    revoked: "Der Schlüssel gilt nicht mehr - es gibt einen neueren, oder das Passwort wurde inzwischen geändert.",
    unknown: "Diesen Schlüssel kennt der Hub nicht. Bitte prüfen oder einen neuen anfordern.",
};

/** What the hub's answer to confirm means. Total over status and body. */
export function confirmOutcome(status: number, body: unknown): ConfirmOutcome {
    const fields = (typeof body === "object" && body !== null ? body : {}) as { state?: unknown; message?: unknown };
    const state = typeof fields.state === "string" ? fields.state : "";
    if (status === 200 && state === "done") return { kind: "done" };
    if (status === 400 && state === "policy") {
        return { kind: "policy", message: typeof fields.message === "string" ? fields.message : "Das Passwort genügt der Regel nicht." };
    }
    if (status === 410) return { kind: "gone", message: GONE[state] ?? GONE.unknown };
    return { kind: "failed", message: "Das Passwort konnte nicht gesetzt werden (HTTP " + status + ")." };
}
